import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, CircularProgress } from '@material-ui/core';
import { MdClose } from 'react-icons/md';
import { toast } from 'react-toastify';
import moment from 'moment';
import { updateEnrollment, getEnrollmentStatuses, getEnrollmentConstants, createEnrollmentConstant } from '../../../../../services/apiService';

const PRIMARY = '#4F46E5';
const FONT = "'Gellix', 'Inter', sans-serif";
const BORDER = '#E5E7EB';

const STATUS_FALLBACK = ['Pending', 'Active', 'Completed', 'Cancelled', 'Failed'];

const ALL_EDITABLE_FIELDS = [
  'saleOwner', 'saleManager', 'product', 'course',
  'masterSeatBookingAmount', 'downPaymentAmount', 'downPaymentSource',
  'loanFinancingSource', 'loanAmount', 'loanTenureMonths',
  'financingPersonPhone', 'financingPersonEmail', 'loanApplicationId', 'financeUTR',
  'saleAmount', 'enrollmentStatus', 'paidAmount', 'placementAmount', 'scheduleAt', 'notes',
  'messageByScript',
];

const inputStyle = {
  flex: 1, height: 36, backgroundColor: '#fff',
  border: `1px solid ${BORDER}`, borderRadius: 4, padding: '0 10px',
  fontSize: 13, color: '#111827', outline: 'none',
  boxSizing: 'border-box', fontFamily: FONT,
};

function Section({ title }) {
  return (
    <div style={{
      fontSize: 14, fontWeight: 700, color: '#111827',
      fontFamily: FONT, marginBottom: 16, paddingBottom: 10,
      borderBottom: `1px solid ${BORDER}`,
    }}>
      {title}
    </div>
  );
}

function SectionDivider() {
  return <div style={{ borderTop: '1px solid #F3F4F6', margin: '20px 0' }} />;
}

function InfoRow({ label, value, editable, field, onChange, type, options }) {
  const labelEl = (
    <span style={{ flex: '0 0 220px', fontSize: 13, fontWeight: 600, color: '#6B7280', fontFamily: FONT }}>
      {label}
    </span>
  );

  if (!editable) {
    let display = value || '-';
    if (type === 'time' && value) display = moment(value).format('HH:mm');
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 14 }}>
        {labelEl}
        <span style={{ fontSize: 13, color: '#111827', fontFamily: FONT }}>{display}</span>
      </div>
    );
  }

  let input;
  if (type === 'select') {
    const safeOptions = Array.isArray(options) ? options : [];
    const valueMissing = value && !safeOptions.includes(value);
    input = (
      <select
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
      >
        <option value="">Select</option>
        {valueMissing && <option value={value}>{value}</option>}
        {safeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  } else if (type === 'time') {
    const datePart = value ? moment(value).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
    input = (
      <input
        type="time"
        value={value ? moment(value).format('HH:mm') : ''}
        onChange={(e) => onChange(field, `${datePart}T${e.target.value}`)}
        style={inputStyle}
      />
    );
  } else {
    input = (
      <input
        value={value || ''}
        onChange={(e) => onChange(field, e.target.value)}
        style={inputStyle}
      />
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
      {labelEl}
      {input}
    </div>
  );
}

export default function InformationModal({ open, onClose, data, editable = false }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [statusOptions, setStatusOptions] = useState(STATUS_FALLBACK);
  const [downPaymentSources, setDownPaymentSources] = useState([]);
  const [financeSources, setFinanceSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [courses, setCourses] = useState([]);
  const [newDownPaymentSource, setNewDownPaymentSource] = useState('');
  const [newFinanceSource, setNewFinanceSource] = useState('');

  useEffect(() => {
    if (data) setForm({ ...data });
  }, [data]);

  useEffect(() => {
    if (!open || !editable) return;
    getEnrollmentStatuses()
      .then((res) => {
        const list = Array.isArray(res) ? res : res?.data;
        if (Array.isArray(list) && list.length) setStatusOptions(list);
      })
      .catch(() => {});
    getEnrollmentConstants()
      .then((res) => {
        // Response shape: { success: true, data: { PRODUCT: [...], COURSE: [...],
        //   DOWN_PAYMENT_SOURCE: [...], FINANCE_SOURCE: [...] } }
        const constants = res?.data ?? res ?? {};
        if (Array.isArray(constants?.PRODUCT) && constants.PRODUCT.length) {
          setProducts(constants.PRODUCT);
        }
        if (Array.isArray(constants?.COURSE) && constants.COURSE.length) {
          setCourses(constants.COURSE);
        }
        if (Array.isArray(constants?.DOWN_PAYMENT_SOURCE) && constants.DOWN_PAYMENT_SOURCE.length) {
          setDownPaymentSources(constants.DOWN_PAYMENT_SOURCE);
        }
        if (Array.isArray(constants?.FINANCE_SOURCE) && constants.FINANCE_SOURCE.length) {
          setFinanceSources(constants.FINANCE_SOURCE);
        }
      })
      .catch(() => {});
  }, [open, editable]);

  const handleAddDownPaymentSource = async () => {
    const name = newDownPaymentSource.trim();
    if (!name) return;
    setDownPaymentSources(prev => prev.includes(name) ? prev : [...prev, name]);
    setForm(f => ({ ...f, downPaymentSource: name }));
    setNewDownPaymentSource('');
    try {
      await createEnrollmentConstant({ name, type: 'DOWN_PAYMENT_SOURCE' });
    } catch (err) {
      console.error('Failed to save down payment source constant', err);
    }
  };

  const handleAddFinanceSource = async () => {
    const name = newFinanceSource.trim();
    if (!name) return;
    setFinanceSources(prev => prev.includes(name) ? prev : [...prev, name]);
    setForm(f => ({ ...f, loanFinancingSource: name }));
    setNewFinanceSource('');
    try {
      await createEnrollmentConstant({ name, type: 'FINANCE_SOURCE' });
    } catch (err) {
      console.error('Failed to save finance source constant', err);
    }
  };

  const isCompleted = data?.enrollmentStatus === 'Completed';
  const canEdit = editable && !isCompleted;

  const handleChange = (field, value) => {
    setForm((f) => {
      const next = { ...f, [field]: value };
      if (field === 'enrollmentStatus') next.messageByScript = null;
      return next;
    });
  };

  const handleSave = async () => {
    if (!data?.id) return;
    setSaving(true);
    try {
      const payload = {};
      ALL_EDITABLE_FIELDS.forEach((f) => { if (form[f] !== undefined) payload[f] = form[f]; });
      await updateEnrollment(data.id, payload);
      toast.success('Enrollment updated successfully');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to update enrollment');
    } finally {
      setSaving(false);
    }
  };

  const row = (label, field, type, options) => (
    <InfoRow
      key={field}
      label={label}
      value={form[field]}
      editable={canEdit}
      field={field}
      onChange={handleChange}
      type={type}
      options={options}
    />
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ style: { borderRadius: 16, overflow: 'hidden', fontFamily: FONT } }}
    >
      <DialogContent style={{ padding: 0 }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 24px 16px', borderBottom: '1px solid #F3F4F6',
        }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: '#111827', margin: 0, fontFamily: FONT }}>
            {editable ? 'Edit Enrolment' : 'Enrolment Details'}
          </h2>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: '#6B7280', borderRadius: 4 }}
          >
            <MdClose size={20} />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px', overflowY: 'auto', maxHeight: '65vh' }}>

          <Section title="Ownership & Program" />
          {row('Sale Owner', 'saleOwner')}
          {row('Sale Manager', 'saleManager')}
          {row('Program', 'product', 'select', products)}
          {row('Course', 'course', 'select', courses)}

          <SectionDivider />

          <Section title="Payment" />
          {row('Seat Booking Amount', 'masterSeatBookingAmount')}
          {row('Down Payment Amount', 'downPaymentAmount')}
          {row('Down Payment Source', 'downPaymentSource', 'select', downPaymentSources)}
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, marginLeft: 220 }}>
              <input
                placeholder="Add new down payment source"
                value={newDownPaymentSource}
                onChange={(e) => setNewDownPaymentSource(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddDownPaymentSource()}
                style={{ ...inputStyle, marginRight: 8 }}
              />
              <button
                onClick={handleAddDownPaymentSource}
                style={{
                  fontSize: 12, fontWeight: 600, color: '#fff', backgroundColor: PRIMARY,
                  border: 'none', borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontFamily: FONT,
                  whiteSpace: 'nowrap',
                }}
              >
                Add
              </button>
            </div>
          )}

          <SectionDivider />

          <Section title="Loan & Financing Details" />
          {row('Loan Financing Source', 'loanFinancingSource', 'select', financeSources)}
          {canEdit && (
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, marginLeft: 220 }}>
              <input
                placeholder="Add new finance source"
                value={newFinanceSource}
                onChange={(e) => setNewFinanceSource(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddFinanceSource()}
                style={{ ...inputStyle, marginRight: 8 }}
              />
              <button
                onClick={handleAddFinanceSource}
                style={{
                  fontSize: 12, fontWeight: 600, color: '#fff', backgroundColor: PRIMARY,
                  border: 'none', borderRadius: 4, padding: '6px 12px', cursor: 'pointer', fontFamily: FONT,
                  whiteSpace: 'nowrap',
                }}
              >
                Add
              </button>
            </div>
          )}
          {row('Loan Amount', 'loanAmount')}
          {row('Loan Tenure (Months)', 'loanTenureMonths')}
          {row("Financing Person's Phone", 'financingPersonPhone')}
          {row("Financing Person's Email", 'financingPersonEmail')}
          {row('Loan Application ID', 'loanApplicationId')}
          {row('Finance UTR', 'financeUTR')}

          <SectionDivider />

          <Section title="Summary & Other Information" />
          {row('Sale Amount', 'saleAmount')}
          {row('Enrollment Status', 'enrollmentStatus', 'select', statusOptions)}
          {row('Message Script', 'messageByScript')}
          {row('Scheduled At', 'scheduleAt', 'time')}
          {row('Paid Amount', 'paidAmount')}
          {row('Placement Amount', 'placementAmount')}
          {row('Other Remarks', 'notes')}

        </div>

        {/* Footer */}
        {editable && (
          <div style={{
            display: 'flex', justifyContent: 'flex-end', gap: 12,
            padding: '16px 24px', borderTop: '1px solid #F3F4F6',
          }}>
            <button
              onClick={onClose}
              style={{
                fontSize: 13, fontWeight: 600, color: '#374151', backgroundColor: '#fff',
                border: `1px solid ${BORDER}`, borderRadius: 4, padding: '8px 20px',
                cursor: 'pointer', fontFamily: FONT,
              }}
            >
              Cancel
            </button>
            {!isCompleted && (
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  fontSize: 13, fontWeight: 600, color: '#fff', backgroundColor: PRIMARY,
                  border: 'none', borderRadius: 4, padding: '8px 20px',
                  cursor: saving ? 'default' : 'pointer', opacity: saving ? 0.7 : 1,
                  fontFamily: FONT, display: 'flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(79,70,229,0.25)',
                }}
              >
                {saving && <CircularProgress size={12} style={{ color: '#fff' }} />}
                Save Changes
              </button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
